import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCcw, Save, Image as ImageIcon, X, Upload, Loader2 } from "lucide-react";

/**
 * SuperAdminSponsors — Iter 58 (Feb 2026).
 *
 * Gestione sponsor platform (template globali). Permette a un superadmin di
 * modificare logo_url, nome, descrizione, categoria e importi senza dover
 * intervenire manualmente sul DB. Le modifiche al logo si propagano anche
 * alle copie non-platform (istanze acquistate in lega).
 */
export default function SuperAdminSponsors() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // sponsor doc in modifica
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/sponsors");
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    const payload = {
      name: editing.name,
      description: editing.description || "",
      logo_url: editing.logo_url || "",
      category: editing.category || "",
      base_amount: Number(editing.base_amount ?? 0),
      placement_bonus: Number(editing.placement_bonus ?? 0),
    };
    try {
      const { data } = await api.patch(`/superadmin/sponsors/${editing.sponsor_id}`, payload);
      toast.success(`✅ Aggiornato · propagato a ${data.propagated_to_league_copies} copie in lega`);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(png|jpeg|jpg|webp|svg\+xml)/.test(file.type)) {
      toast.error("Formato non supportato. Usa PNG/JPG/WEBP/SVG.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error(`File troppo grande (max 3 MB, ricevuti ${(file.size / 1024 / 1024).toFixed(1)} MB)`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/logo?scope=sponsor", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Trasforma URL relativo in assoluto (per <img src> cross-origin)
      const absUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setEditing(x => ({ ...x, logo_url: absUrl }));
      toast.success("✅ Logo caricato — clicca Salva per applicarlo");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="p-6 sm:p-8 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight" data-testid="sa-sponsors-title">Gestione Sponsor</h1>
        <button onClick={load} data-testid="sa-sponsors-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-700 hover:border-rose-500 hover:bg-rose-500/10 text-sm text-slate-300">
          <RefreshCcw size={14}/> Ricarica
        </button>
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-500">Nessuno sponsor</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-3">
            {rows.map(s => (
              <div key={s.sponsor_id} data-testid={`sa-sponsor-card-${s.sponsor_id}`}
                className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 space-y-2">
                <div className="flex items-center gap-3">
                  {s.logo_url ? (
                    <img src={s.logo_url} alt={s.name}
                      className="w-14 h-14 rounded-md object-cover bg-slate-900 border border-slate-800"
                      onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}/>
                  ) : (
                    <div className="w-14 h-14 rounded-md bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-600">
                      <ImageIcon size={20}/>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm text-slate-100 truncate">{s.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono truncate">{s.sponsor_id}</div>
                    {s.category && <div className="text-[10px] text-slate-400 uppercase tracking-widest">{s.category}</div>}
                  </div>
                </div>
                <div className="text-xs text-slate-400 line-clamp-2">{s.description || <em className="text-slate-600">Nessuna descrizione</em>}</div>
                <div className="flex items-center justify-between text-[11px] font-mono text-slate-500">
                  <span>Base: <b className="text-emerald-400">{s.base_amount || 0}cr</b></span>
                  <span>Piazz: <b className="text-amber-400">{s.placement_bonus || 0}cr</b></span>
                </div>
                <button onClick={() => setEditing({...s})} data-testid={`sa-sponsor-edit-${s.sponsor_id}`}
                  className="w-full mt-1.5 px-3 py-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-xs font-bold">
                  Modifica
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setEditing(null)} data-testid="sa-sponsor-modal">
          <div className="bg-slate-950 border border-slate-700 rounded-2xl max-w-lg w-full p-5 space-y-3"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black">Modifica sponsor</h2>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-white"><X size={20}/></button>
            </div>
            <div className="text-[10px] text-slate-500 font-mono">{editing.sponsor_id}</div>

            {editing.logo_url && (
              <img src={editing.logo_url} alt="preview"
                className="w-24 h-24 rounded-lg object-cover mx-auto bg-slate-900 border border-slate-800"
                onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}/>
            )}

            {/* Iter 58: Direct file upload (PNG/JPG → R2) */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 flex items-center justify-between">
                <span>Carica logo da file</span>
                <span className="text-slate-600 normal-case tracking-normal">max 3 MB · PNG/JPG/WEBP/SVG</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="sa-sponsor-file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="sa-sponsor-upload-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-dashed border-slate-600 hover:border-rose-500 hover:bg-rose-500/5 text-sm text-slate-300 disabled:opacity-50"
              >
                {uploading ? <><Loader2 size={14} className="animate-spin"/> Caricamento…</> : <><Upload size={14}/> Scegli immagine…</>}
              </button>
            </div>

            {[
              { key: "name",           label: "Nome",              type: "text" },
              { key: "logo_url",       label: "URL logo (asset)",  type: "url",  placeholder: "https://…" },
              { key: "category",       label: "Categoria",         type: "text", placeholder: "es. Tecnologia" },
              { key: "description",    label: "Descrizione",       type: "textarea" },
              { key: "base_amount",    label: "Importo Base (cr)", type: "number" },
              { key: "placement_bonus",label: "Bonus Piazzamento (cr)", type: "number" },
            ].map(f => (
              <div key={f.key}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{f.label}</div>
                {f.type === "textarea" ? (
                  <textarea value={editing[f.key] || ""}
                    onChange={e => setEditing(x => ({...x, [f.key]: e.target.value}))}
                    rows={2}
                    data-testid={`sa-sponsor-field-${f.key}`}
                    className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm focus:border-rose-500 outline-none"/>
                ) : (
                  <input type={f.type} value={editing[f.key] ?? ""}
                    placeholder={f.placeholder}
                    onChange={e => setEditing(x => ({...x, [f.key]: e.target.value}))}
                    data-testid={`sa-sponsor-field-${f.key}`}
                    className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm focus:border-rose-500 outline-none"/>
                )}
              </div>
            ))}

            <div className="flex items-center gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="flex-1 px-3 py-2 rounded-md border border-slate-700 text-slate-300 text-sm">Annulla</button>
              <button onClick={save} data-testid="sa-sponsor-save"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-gradient-to-br from-rose-500 to-pink-600 text-white font-black text-sm hover:brightness-110">
                <Save size={14}/> Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
